import {
  createRouter,
  createRoute,
  createRootRoute,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { AppShell } from "@/components/layout/AppShell";
import { LoginPage } from "@/pages/LoginPage";
import { AuthCallbackPage } from "@/pages/AuthCallbackPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { WorkspacePage } from "@/pages/WorkspacePage";
import { BoardPage } from "@/pages/BoardPage";
import { SystemPage } from "@/pages/SystemPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { SubmitPage, SubmitDankePage } from "@/pages/SubmitPage";
import { PostfachPage } from "@/pages/PostfachPage";
import { IdeenPage } from "@/pages/IdeenPage";
import { MeetingsPage } from "@/pages/MeetingsPage";
import { SeriesPage } from "@/pages/SeriesPage";
import { MemoPage } from "@/pages/MemoPage";
import { PrintMemoPage } from "@/pages/PrintMemoPage";
import { authStore } from "@/lib/authStore";
import { RouteErrorFallback } from "@/components/ui/RouteErrorFallback";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => {
    const raw = search["returnTo"];
    if (typeof raw === "string" && raw.startsWith("/")) return { returnTo: raw };
    return {};
  },
  component: LoginPage,
});

const callbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: AuthCallbackPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "protected",
  beforeLoad: ({ location }) => {
    if (!authStore.isAuthenticated()) {
      const returnTo = location.pathname + (location.searchStr ?? "");
      throw redirect({
        to: "/login",
        search: returnTo && returnTo !== "/" ? { returnTo } : {},
      });
    }
  },
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/",
  component: DashboardPage,
});

const workspaceRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/w/$workspaceId",
  component: WorkspacePage,
});

type BoardView = "table" | "kanban" | "calendar" | "timeline";

const boardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/b/$boardId",
  validateSearch: (
    search: Record<string, unknown>,
  ): { view?: BoardView; row?: string; comment?: string; q?: string } => {
    const out: { view?: BoardView; row?: string; comment?: string; q?: string } = {};
    const raw = search["view"];
    if (raw === "table" || raw === "kanban" || raw === "calendar" || raw === "timeline")
      out.view = raw;
    const row = search["row"];
    if (typeof row === "string" && row.length > 0) out.row = row;
    const comment = search["comment"];
    if (typeof comment === "string" && comment.length > 0) out.comment = comment;
    // The search term that led to a comment hit — highlighted inside the flashed comment.
    const q = search["q"];
    if (typeof q === "string" && q.length > 0) out.q = q;
    return out;
  },
  component: BoardPage,
});

const systemRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/system",
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw redirect({ to: "/" });
  },
  component: SystemPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/settings",
  component: SettingsPage,
});

const postfachRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/postfach",
  component: PostfachPage,
});

const ideenRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/ideen",
  component: IdeenPage,
});

const submitRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/submit/$boardId",
  component: SubmitPage,
});

const submitDankeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/submit/$boardId/danke",
  component: SubmitDankePage,
});

const meetingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/meetings",
  component: MeetingsPage,
});

const seriesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/meetings/$seriesId",
  component: SeriesPage,
});

const memoRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/memo/$memoId",
  validateSearch: (
    search: Record<string, unknown>,
  ): { entry?: string; block?: string; q?: string } => {
    const out: { entry?: string; block?: string; q?: string } = {};
    const entry = search["entry"];
    if (typeof entry === "string" && entry.length > 0) out.entry = entry;
    const block = search["block"];
    if (typeof block === "string" && block.length > 0) out.block = block;
    const q = search["q"];
    if (typeof q === "string" && q.length > 0) out.q = q;
    return out;
  },
  component: MemoPage,
});

// A sibling of `protectedRoute` that shares the EXACT same auth guard but whose
// component is a bare <Outlet /> — no AppShell. The print route lives here so the
// Protokoll renders chrome-less (no sidebar/topbar) while still behind auth.
const bareProtectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "bare-protected",
  beforeLoad: protectedRoute.options.beforeLoad,
  component: () => <Outlet />,
});

const memoPrintRoute = createRoute({
  getParentRoute: () => bareProtectedRoute,
  path: "/memo/$memoId/print",
  component: PrintMemoPage,
});

const protectedChildren = [
  dashboardRoute,
  workspaceRoute,
  boardRoute,
  meetingsRoute,
  seriesRoute,
  memoRoute,
  systemRoute,
  settingsRoute,
  postfachRoute,
  ideenRoute,
  submitRoute,
  submitDankeRoute,
];

const routeTree = rootRoute.addChildren([
  loginRoute,
  callbackRoute,
  protectedRoute.addChildren(protectedChildren),
  bareProtectedRoute.addChildren([memoPrintRoute]),
]);

export const router = createRouter({
  routeTree,
  // Chunk-load failures (a lazy part of an old build gone after a deploy) get a German
  // message with "Neu laden"; every other error keeps the router's default box.
  defaultErrorComponent: RouteErrorFallback,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
